// BroadcastChannel Hook for Window-to-Window Communication
// Used for Staff POS ↔ Customer Display communication

import { useEffect, useRef, useCallback } from 'react'
import type { CounterData } from '@abcpay/shared'

export type POSMessageType =
  | 'TRANSACTION_UPDATE'
  | 'EXCHANGE_RATE_UPDATE'
  | 'DISPLAY_MODE_CHANGE'
  | 'COUNTER_DATA'
  | 'PING'
  | 'PONG'

export interface POSMessage<T = unknown> {
  type: POSMessageType
  payload: T
  senderId: string
  timestamp: number
}

// 다중 통화 거래의 통화별 라인 (#5)
export interface TransactionCurrencyLine {
  currency: string
  amount: number
  rate: number
  krwAmount: number
}

export interface TransactionUpdatePayload {
  id?: string
  amount: number
  currency: string
  rate: number
  krwAmount: number
  tradeType: 'buy' | 'sell'
  status: 'idle' | 'processing' | 'complete' | 'rate-preview'
  // 다중 통화 거래 시 통화별 라인과 원화 총액
  lines?: TransactionCurrencyLine[]
  totalKRW?: number
  // 통화간 교환 거래 (#7): 손님이 내는 통화 / 받는 통화 / 원화 거스름돈
  exchange?: {
    give: TransactionCurrencyLine
    receive: TransactionCurrencyLine
    changeKRW: number
  }
}

export interface ExchangeRateUpdatePayload {
  rates: Record<string, {
    buy: number
    sell: number
    currency: string
  }>
  branchName?: string
}

export interface DisplayModeChangePayload {
  mode: 'idle' | 'transaction' | 'complete'
}

export type CounterDataPayload = Omit<CounterData, 'timestamp'> & { timestamp?: number }

const CHANNEL_NAME = 'pos-state'

interface UseBroadcastChannelOptions {
  onMessage?: (message: POSMessage) => void
  windowId?: string
}

export function useBroadcastChannel(options: UseBroadcastChannelOptions = {}) {
  const channelRef = useRef<BroadcastChannel | null>(null)
  const windowIdRef = useRef(options.windowId || `window-${crypto.randomUUID().slice(0, 8)}`)

  // Initialize channel
  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME)
    channelRef.current = channel

    channel.onmessage = (event: MessageEvent<POSMessage>) => {
      const message = event.data

      // Ignore own messages
      if (message.senderId === windowIdRef.current) {
        return
      }

      options.onMessage?.(message)
    }

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [options.onMessage])

  // Send message to other windows
  const send = useCallback(<T>(type: POSMessageType, payload: T) => {
    if (!channelRef.current) {
      console.warn('BroadcastChannel not initialized')
      return
    }

    const message: POSMessage<T> = {
      type,
      payload,
      senderId: windowIdRef.current,
      timestamp: Date.now(),
    }

    channelRef.current.postMessage(message)
  }, [])

  // Convenience methods
  const sendTransactionUpdate = useCallback((payload: TransactionUpdatePayload) => {
    send('TRANSACTION_UPDATE', payload)
  }, [send])

  const sendExchangeRateUpdate = useCallback((payload: ExchangeRateUpdatePayload) => {
    send('EXCHANGE_RATE_UPDATE', payload)
  }, [send])

  const sendDisplayModeChange = useCallback((payload: DisplayModeChangePayload) => {
    send('DISPLAY_MODE_CHANGE', payload)
  }, [send])

  const sendCounterData = useCallback((payload: CounterDataPayload) => {
    send('COUNTER_DATA', payload)
  }, [send])

  const ping = useCallback(() => {
    send('PING', { windowId: windowIdRef.current })
  }, [send])

  return {
    windowId: windowIdRef.current,
    send,
    sendTransactionUpdate,
    sendExchangeRateUpdate,
    sendDisplayModeChange,
    sendCounterData,
    ping,
  }
}

// Hook for Customer Display (receive-only)
export function useCustomerDisplayChannel(
  onTransactionUpdate?: (payload: TransactionUpdatePayload) => void,
  onExchangeRateUpdate?: (payload: ExchangeRateUpdatePayload) => void,
  onDisplayModeChange?: (payload: DisplayModeChangePayload) => void,
  onCounterData?: (payload: CounterDataPayload) => void,
) {
  const handleMessage = useCallback((message: POSMessage) => {
    switch (message.type) {
      case 'TRANSACTION_UPDATE':
        onTransactionUpdate?.(message.payload as TransactionUpdatePayload)
        break
      case 'EXCHANGE_RATE_UPDATE':
        onExchangeRateUpdate?.(message.payload as ExchangeRateUpdatePayload)
        break
      case 'DISPLAY_MODE_CHANGE':
        onDisplayModeChange?.(message.payload as DisplayModeChangePayload)
        break
      case 'COUNTER_DATA':
        onCounterData?.(message.payload as CounterDataPayload)
        break
    }
  }, [onTransactionUpdate, onExchangeRateUpdate, onDisplayModeChange, onCounterData])

  return useBroadcastChannel({
    onMessage: handleMessage,
    windowId: 'customer-display',
  })
}
